import {
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  LayoutGrid,
  List,
  Search,
} from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useRecordGroupSearch } from '@/hooks/useRecordGroups'
import { useUrlState } from '@/hooks/useUrlState'
import type {
  RecordGroupListItem,
  RecordGroupSearchScope,
  RecordGroupSortField,
  SortDirection,
} from '@/types/api/record-group'
import { cn } from '@/utils/cn'

type ViewMode = 'rows' | 'cards'

const GROUP_PAGE_SIZES = [12, 24, 48, 96] as const

const SORT_OPTIONS: { value: string; label: string; sort: RecordGroupSortField; dir: SortDirection }[] = [
  { value: 'NAME:ASC', label: 'Name A–Z', sort: 'NAME', dir: 'ASC' },
  { value: 'NAME:DESC', label: 'Name Z–A', sort: 'NAME', dir: 'DESC' },
  { value: 'CREATED_AT:DESC', label: 'Newest first', sort: 'CREATED_AT', dir: 'DESC' },
  { value: 'CREATED_AT:ASC', label: 'Oldest first', sort: 'CREATED_AT', dir: 'ASC' },
  { value: 'UPDATED_AT:DESC', label: 'Recently updated', sort: 'UPDATED_AT', dir: 'DESC' },
]

const ROW_ITEM_CLASS = 'group flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-2 py-3 hover:bg-accent/50'
const CARD_CLASS = 'border-neutral-300 bg-background text-foreground shadow-none hover:shadow-none'
const CARD_CONTENT_CLASS = 'group flex min-w-0 items-start gap-3 p-4'
const CARD_GRID_CLASS = 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3'

const URL_DEFAULTS = {
  q: '',
  view: 'cards',
  page: 0,
  size: 24,
  scope: '',
  order: 'NAME:ASC',
}

interface RecordGroupBrowserProps {
  /** Lists the children of this group; the top level when omitted. */
  parentId?: string
  lockedResourceIds: Set<string>
  /** Namespace for the URL params, so several lists can share one page. */
  urlPrefix?: string
  /** Rendered left of the search box (e.g. a section title). */
  heading?: ReactNode
  /** Extra controls rendered at the end of the toolbar (e.g. "Add child group"). */
  actions?: ReactNode
  className?: string
  /** Toolbar sticks to the top of the scroll container (overview page). */
  stickyToolbar?: boolean
}

function GroupBadges({ group }: { group: RecordGroupListItem }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="secondary">
        {group.childCount} child {group.childCount === 1 ? 'group' : 'groups'}
      </Badge>
      {group.recordCount !== null && (
        <Badge variant="secondary" className="tabular-nums">
          {group.recordCount} {group.recordCount === 1 ? 'record' : 'records'}
        </Badge>
      )}
      {group.traceOnly ? (
        <Badge
          variant="outline"
          className="whitespace-nowrap"
          title="You have access to some items inside this group, not to the group itself"
        >
          Limited access
        </Badge>
      ) : group.formId ? (
        <Badge variant="success" className="gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          Form configured
        </Badge>
      ) : (
        <Badge variant="secondary">No form</Badge>
      )}
    </div>
  )
}

function GroupIcon() {
  return (
    <span className="relative shrink-0 text-muted-foreground transition-colors group-hover:text-primary">
      <Folder className="h-5 w-5 transition-opacity group-hover:opacity-0" />
      <FolderOpen className="absolute inset-0 h-5 w-5 opacity-0 transition-opacity group-hover:opacity-100" />
    </span>
  )
}

/** "Parent › Child" trail for results found below the listed level. */
function GroupPath({ group, parentId }: { group: RecordGroupListItem; parentId?: string }) {
  // Hide the part of the path the user is already in.
  const startIndex = parentId ? group.path.findIndex((p) => p.id === parentId) + 1 : 0
  const path = group.path.slice(Math.max(startIndex, 0))
  if (path.length === 0) return null
  return (
    <span className="flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
      {path.map((p, i) => (
        <span key={p.id} className="inline-flex min-w-0 items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
          <span className="truncate">{p.name}</span>
        </span>
      ))}
    </span>
  )
}

function RecordGroupItem({
  group,
  parentId,
  isLocked,
  viewMode,
}: {
  group: RecordGroupListItem
  parentId?: string
  isLocked: boolean
  viewMode: ViewMode
}) {

  if (viewMode === 'rows') {
    return (
      <Link to={`/record-groups/${group.id}`} className={ROW_ITEM_CLASS}>
        <GroupIcon />
        <span className="flex min-w-0 flex-col">
          <span className="font-medium">{group.name}</span>
          <GroupPath group={group} parentId={parentId} />
        </span>
        {isLocked && <Badge variant="outline">Locked</Badge>}
        <GroupBadges group={group} />
      </Link>
    )
  }

  return (
    <Card variant="interactive" className={group.traceOnly ? `${CARD_CLASS} border-dashed` : CARD_CLASS}>
      <Link to={`/record-groups/${group.id}`} className={CARD_CONTENT_CLASS}>
        <GroupIcon />
        <span className="min-w-0 flex-1">
          <span className="mb-2 flex min-w-0 flex-col">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate font-semibold">{group.name}</span>
              {isLocked && <Badge variant="outline" className="shrink-0">Locked</Badge>}
            </span>
            <GroupPath group={group} parentId={parentId} />
          </span>
          <GroupBadges group={group} />
        </span>
      </Link>
    </Card>
  )
}

function RecordGroupItemSkeleton({ viewMode }: { viewMode: ViewMode }) {
  if (viewMode === 'rows') {
    return (
      <div className={ROW_ITEM_CLASS}>
        <Skeleton className="h-5 w-5 shrink-0 rounded-sm" />
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-28 rounded-full" />
      </div>
    )
  }

  return (
    <Card variant="interactive" className={CARD_CLASS}>
      <div className={CARD_CONTENT_CLASS}>
        <Skeleton className="h-5 w-5 shrink-0 rounded-sm" />
        <div className="min-w-0 flex-1 space-y-3">
          <Skeleton className="h-5 w-36" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-28 rounded-full" />
            <Skeleton className="h-6 w-28 rounded-full" />
          </div>
        </div>
      </div>
    </Card>
  )
}

/**
 * Paged, server-searched record group list (cards or rows). Used for the top level on the Record
 * Groups page and for child groups on a group's page. Search covers every nested level by default
 * and shows each nested result's path.
 */
export function RecordGroupBrowser({
  parentId,
  lockedResourceIds,
  urlPrefix = 'g',
  heading,
  actions,
  className,
  stickyToolbar = false,
}: RecordGroupBrowserProps) {
  const [state, setState] = useUrlState(urlPrefix, URL_DEFAULTS)
  const [searchInput, setSearchInput] = useState(state.q)
  const debouncedSearch = useDebouncedValue(searchInput, 300)
  // Last query this instance agreed with the URL on; only text typed here is pushed, so another
  // instance on the page (e.g. the mobile layout) or Back/Forward is never overwritten.
  const syncedQuery = useRef(state.q)

  // Push the debounced input to the URL (and back to page 1) once typing pauses.
  useEffect(() => {
    if (debouncedSearch !== syncedQuery.current) {
      syncedQuery.current = debouncedSearch
      setState({ q: debouncedSearch, page: 0 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // Follow URL changes made elsewhere.
  useEffect(() => {
    syncedQuery.current = state.q
    setSearchInput(state.q)
  }, [state.q])

  const viewMode: ViewMode = state.view === 'rows' ? 'rows' : 'cards'
  const order = SORT_OPTIONS.find((o) => o.value === state.order) ?? SORT_OPTIONS[0]
  const searching = state.q.trim().length > 0
  const scope = (state.scope || undefined) as RecordGroupSearchScope | undefined
  const effectiveScope: RecordGroupSearchScope = scope ?? (searching ? 'SUBTREE' : 'CHILDREN')

  const { data, isLoading, isFetching } = useRecordGroupSearch({
    q: state.q,
    parentId,
    scope,
    sort: order.sort,
    dir: order.dir,
    page: state.page,
    size: state.size,
  })

  // Clamp to the last page when results shrink (e.g. after a delete).
  useEffect(() => {
    if (data && data.totalPages > 0 && state.page > data.totalPages - 1) {
      setState({ page: data.totalPages - 1 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.totalPages])

  const groups = data?.content ?? []
  // A nested browser is used inside the fixed-width group sidebar. Keep its cards
  // in one column even when the overall desktop viewport is wide.
  const itemsClassName = viewMode === 'rows'
    ? ''
    : cn(CARD_GRID_CLASS, parentId && 'grid-cols-1 sm:grid-cols-1 lg:grid-cols-1')
  const placeholder = parentId ? 'Search groups in this group…' : 'Search record groups…'

  return (
    <div className={className}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 pb-3',
          stickyToolbar && 'sticky top-0 z-10 bg-background pl-2 pt-2'
        )}
      >
        {heading}
        <label className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            className="h-10 pl-10 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
          />
        </label>
        {searching && (
          <div className="flex shrink-0 items-center rounded-md border p-0.5" role="group" aria-label="Search depth">
            <Button
              type="button"
              size="sm"
              variant={effectiveScope === 'SUBTREE' ? 'secondary' : 'ghost'}
              className="h-8"
              aria-pressed={effectiveScope === 'SUBTREE'}
              onClick={() => setState({ scope: 'SUBTREE', page: 0 })}
            >
              All levels
            </Button>
            <Button
              type="button"
              size="sm"
              variant={effectiveScope === 'CHILDREN' ? 'secondary' : 'ghost'}
              className="h-8"
              aria-pressed={effectiveScope === 'CHILDREN'}
              onClick={() => setState({ scope: 'CHILDREN', page: 0 })}
            >
              This level
            </Button>
          </div>
        )}
        <Select value={order.value} onValueChange={(value) => setState({ order: value, page: 0 })}>
          <SelectTrigger className="h-10 w-[10.5rem] shrink-0" aria-label="Sort record groups">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
            aria-label="Show record groups as cards"
            aria-pressed={viewMode === 'cards'}
            onClick={() => setState({ view: 'cards' })}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant={viewMode === 'rows' ? 'secondary' : 'ghost'}
            aria-label="Show record groups as rows"
            aria-pressed={viewMode === 'rows'}
            onClick={() => setState({ view: 'rows' })}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
        {actions}
      </div>

      {isLoading ? (
        <div className={itemsClassName}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <RecordGroupItemSkeleton key={item} viewMode={viewMode} />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          {searching ? 'No record groups match your search.' : parentId ? 'No child groups yet.' : 'No record groups yet.'}
        </div>
      ) : (
        <div className={cn(itemsClassName, isFetching && 'opacity-70 transition-opacity')} aria-busy={isFetching}>
          {groups.map((group) => (
            <RecordGroupItem
              key={group.id}
              group={group}
              parentId={parentId}
              isLocked={lockedResourceIds.has(group.id)}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}

      {data && (
        <Pagination
          page={data.page}
          size={state.size}
          totalElements={data.totalElements}
          totalPages={data.totalPages}
          onPageChange={(page) => setState({ page })}
          onSizeChange={(size) => setState({ size, page: 0 })}
          sizeOptions={GROUP_PAGE_SIZES}
          itemLabel="groups"
        />
      )}
    </div>
  )
}
